/**
 * Change the crew's address (slice 2h §3, §8.2; Paper 75, 75b, 76), the
 * owner's: the name, with the domain fixed after it, checked as they type
 * (free, or taken or reserved with two names that are), and what happens to
 * the old address: it keeps arriving for 90 days. A dialog on a desktop, a
 * sheet on a phone.
 */
import { Button } from "@crew/ui/components/button";
import { cn } from "@crew/ui/lib/utils";
import { Check, Clock } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { errorHint } from "../../app/api";
import { useNameCheck, useRenameAddress, type EmailView } from "./api";
import { checkLine, keptUntil, nameOf } from "./email-view";
import { BIG } from "./window-kit";
import { Footer, Head, WindowShell } from "./window-parts";

/** `value`, once it's held still for `ms`. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

export function ChangeAddress({
  open,
  email,
  timezone,
  mobile,
  onClose,
}: {
  open: boolean;
  email: EmailView;
  timezone: string;
  mobile: boolean;
  onClose: () => void;
}) {
  return (
    <WindowShell open={open} mobile={mobile} onClose={onClose}>
      {open && email.address ? (
        <ChangeForm
          current={email.address.full}
          timezone={timezone}
          mobile={mobile}
          onClose={onClose}
        />
      ) : null}
    </WindowShell>
  );
}

/** The form, apart from its frame so a test can render it. */
/** The name, with the domain fixed after it on a desktop and the whole address under it on a phone. */
function NameField({
  input,
  name,
  oldName,
  domain,
  refused,
  mobile,
  onChange,
}: {
  input: string;
  name: string;
  oldName: string;
  domain: string;
  refused: boolean;
  mobile: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <>
      {mobile ? null : (
        <label htmlFor="address-name" className="text-sm leading-5 font-medium text-foreground">
          Name
        </label>
      )}
      <div
        className={cn(
          "flex overflow-hidden rounded-md border bg-card focus-within:border-[1.5px] focus-within:border-foreground",
          refused ? "border-destructive" : "border-input",
        )}
      >
        <input
          id="address-name"
          aria-label="Name"
          autoFocus
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
          value={input}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={refused || undefined}
          className={cn(
            "min-w-0 flex-1 bg-transparent font-mono text-foreground outline-none",
            mobile ? "p-3 text-base leading-5" : "px-3 py-2.5 text-body leading-5",
          )}
        />
        {mobile ? null : (
          <span className="flex shrink-0 items-center border-l border-input bg-accent px-3 font-mono text-body text-muted-foreground">
            @{domain}
          </span>
        )}
      </div>
      {mobile ? (
        <span className="font-mono text-meta leading-4.5 text-muted-foreground">
          {name || oldName}@{domain}
        </span>
      ) : null}
    </>
  );
}

/** What happens to the old address (Paper 75, 75b): it keeps arriving for 90 days. */
function KeptNote({
  current,
  timezone,
  mobile,
}: {
  current: string;
  timezone: string;
  mobile: boolean;
}) {
  const [opened] = useState(() => Date.now());
  const until = keptUntil(opened, timezone);
  return (
    <div className="mt-2 flex gap-3 rounded-lg bg-background p-4">
      {mobile ? null : <Clock aria-hidden className="mt-0.5 size-4 shrink-0 text-copper" />}
      <div className="flex flex-col gap-0.5">
        <span className="text-sm leading-5 font-medium text-foreground">
          {mobile
            ? "The old address works for 90 days"
            : "The old address keeps working for 90 days"}
        </span>
        <span className="text-meta leading-4.5 text-muted-foreground">
          {mobile
            ? `Mail to ${current.split("@")[0]}@ still arrives until ${until}. Replies come from the new one.`
            : `Mail to ${current} still reaches your crew until ${until}. Replies come from the new address.`}
        </span>
      </div>
    </div>
  );
}

/** Change address: the sheet's full-width button, or the dialog's footer with Cancel. */
function ChangeActions({
  mobile,
  disabled,
  onClose,
}: {
  mobile: boolean;
  disabled: boolean;
  onClose: () => void;
}) {
  if (mobile) {
    return (
      <div className="px-5 pt-4.5 pb-8.5">
        <Button type="submit" size="lg" className={BIG} disabled={disabled}>
          Change address
        </Button>
      </div>
    );
  }
  return (
    <div className="pt-6">
      <Footer
        line={<span className="text-meta leading-4.5 text-hint">Works in about a minute.</span>}
      >
        <Button type="button" variant="ghost" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="lg" disabled={disabled}>
          Change address
        </Button>
      </Footer>
    </div>
  );
}

/** The name checked as it's typed: once it's held still, a check, and whether it can go. */
function useNameField(oldName: string) {
  const [input, setInput] = useState(oldName);
  const name = nameOf(input);
  const settled = useSettled(name, 250);
  const unchanged = name === oldName;
  const check = useNameCheck(unchanged ? "" : settled);
  const current = settled === name && !unchanged ? check.data : undefined;
  return {
    input,
    setInput,
    name,
    line: checkLine(name, current),
    ready: current?.available === true,
  };
}

export function ChangeForm({
  current,
  timezone,
  mobile,
  onClose,
}: {
  current: string;
  timezone: string;
  mobile: boolean;
  onClose: () => void;
}) {
  const [oldName, domain] = current.split("@") as [string, string];
  const field = useNameField(oldName);
  const rename = useRenameAddress();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (field.ready) rename.mutate(field.name, { onSuccess: onClose });
  };
  return (
    <form onSubmit={submit} data-change-address className="flex flex-col">
      <Head
        mobile={mobile}
        eyebrow="Your crew's address"
        title="Change the address"
        line={mobile ? null : "Everyone in the crew emails this one. Keep it short."}
      />
      <div className={cn("flex flex-col gap-2", mobile ? "px-5 pt-4.5" : "px-7 pt-6")}>
        <NameField
          input={field.input}
          name={field.name}
          oldName={oldName}
          domain={domain}
          refused={field.line.tone === "red"}
          mobile={mobile}
          onChange={field.setInput}
        />
        <CheckLine line={field.line} mobile={mobile} onPick={field.setInput} />
        <KeptNote current={current} timezone={timezone} mobile={mobile} />
        {rename.error ? (
          <p role="alert" className="text-meta leading-4.5 text-destructive">
            {errorHint(rename.error)}
          </p>
        ) : null}
      </div>
      <ChangeActions
        mobile={mobile}
        disabled={!field.ready || rename.isPending}
        onClose={onClose}
      />
    </form>
  );
}

/** Free, or why not and two names that are (Paper 75, 76). */
function CheckLine({
  line,
  mobile,
  onPick,
}: {
  line: ReturnType<typeof checkLine>;
  mobile: boolean;
  onPick: (name: string) => void;
}) {
  if (line.tone === "moss") {
    return (
      <span
        data-name-check="free"
        className="flex items-center gap-1.5 text-meta leading-4.5 text-moss"
      >
        <Check aria-hidden className="size-3.5" />
        {mobile ? "It's free" : line.text}
        {mobile ? null : <span className="text-hint">· letters, numbers and dashes, 3 to 30</span>}
      </span>
    );
  }
  if (line.tone === "hint") {
    return <span className="text-meta leading-4.5 text-hint">{line.text}</span>;
  }
  return (
    <span data-name-check="refused" role="alert" className="text-meta leading-4.5 text-destructive">
      {line.text}
      {line.suggestions.length ? (
        <span className="text-muted-foreground">
          {" "}
          Try{" "}
          {line.suggestions.map((s, i) => (
            <span key={s}>
              {i > 0 ? " or " : null}
              <button
                type="button"
                className="font-mono text-foreground underline underline-offset-2"
                onClick={() => onPick(s)}
              >
                {s}
              </button>
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
}
